import React, { useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import Papa from 'papaparse';

const ImportProducts = () => {
  const navigate = useNavigate();
  const { isAdmin } = useContext(AuthContext);
  const { branch } = useParams();

  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Redirect if not admin
  if (!isAdmin()) {
    navigate('/');
    return null;
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setCsvFile(null);
      setCsvData([]);
      setPreviewData([]);
      setShowPreview(false);
      return;
    }
    
    if (!file.name.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      setCsvFile(null);
      return;
    }
    
    setCsvFile(file);
    setError('');
    
    // Parse CSV for preview
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setError('CSV parsing failed: ' + results.errors[0].message);
          return;
        }
        
        setCsvData(results.data);
        setPreviewData(results.data.slice(0, 5)); // Show first 5 rows
        validateCsvData(results.data);
        setShowPreview(true);
      },
      error: (error) => {
        setError('CSV parsing failed: ' + error.message);
      }
    });
  };

  const validateCsvData = (data) => {
    const errors = [];
    
    // Check if required headers exist
    const firstRow = data[0];
    if (!firstRow) {
      errors.push('CSV file is empty');
      setValidationErrors(errors);
      return;
    }
    
    const hasName = 'name' in firstRow;
    const hasItemCode = 'item_code' in firstRow;
    
    if (!hasName || !hasItemCode) {
      errors.push('CSV file must contain "name" and "item_code" columns');
      setValidationErrors(errors);
      return;
    }
    
    // Validate each row
    data.forEach((row, index) => {
      if (!row.name || !row.item_code) {
        errors.push(`Row ${index + 2}: Name and item code are required`);
      }
      
      if (row.quantity && isNaN(parseInt(row.quantity))) {
        errors.push(`Row ${index + 2}: Quantity must be a number`);
      }
    });
    
    setValidationErrors(errors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }
    
    if (validationErrors.length > 0) {
      setError('Please fix the validation errors before importing');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const formData = new FormData();
      formData.append('csv', csvFile);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/products/${branch}/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setSuccess(response.data.message);
        setCsvFile(null);
        setCsvData([]);
        setPreviewData([]);
        setShowPreview(false);
        // Reset file input
        document.getElementById('csvFile').value = '';
      } else {
        setError(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing products:', error);
      setError(error.response?.data?.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h2 className="mb-0">Import Products</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Back to Products
        </button>
      </div>
      
      <div className="card-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success" role="alert">
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="csvFile" className="form-label">CSV File</label>
            <input
              type="file"
              className="form-control"
              id="csvFile"
              accept=".csv"
              onChange={handleFileChange}
              required
            />
            <small className="form-text text-muted">
              The CSV file should have the following columns: name, item_code, quantity
            </small>
          </div>
          
          {validationErrors.length > 0 && (
            <div className="alert alert-warning mb-3">
              <h5>Validation Errors</h5>
              <ul>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          
          {showPreview && (
            <div className="mb-4">
              <h5>CSV Preview (first 5 rows)</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      {Object.keys(previewData[0] || {}).map(header => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {Object.keys(previewData[0] || {}).map(header => (
                          <td key={`${rowIndex}-${header}`}>{row[header]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted">
                Total rows to import: {csvData.length}
              </p>
            </div>
          )}
          
          <div className="mb-4">
            <h5>CSV Format Instructions</h5>
            <p>Your CSV file should follow this format:</p>
            <pre className="bg-light p-3 border rounded">
              name,item_code,quantity<br />
              Product 1,P001,10<br />
              Product 2,P002,20<br />
              Product 3,P003,30
            </pre>
            <p>
              <strong>Note:</strong> For each product, the system will look for an image named [item_code].jpg in the photos directory.
            </p>
          </div>
          
          <div className="d-grid">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || validationErrors.length > 0}
            >
              {loading ? 'Importing...' : 'Import Products'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportProducts;
